import {
    DataType as IapDataType,
    type IUser,
    type IUserProfile,
    type IUserTenantAccess,
    type OrderBy,
    queryBuilder,
    UserCommonCalculatedFields
} from '@inpart-io/devtools-iap';
import { GET_USER_CURRENT, RECEIVE_DATA_OBJECTS, RETRIEVE_BY_QUERY, SESSION_MODULE, STORE_DATA_OBJECTS } from '@inpart-io/devtools-vue';
import { userProfileProperties, userProfilesManager } from '@inpart-io/padme-iap';
import { type ActionTree } from 'vuex';

import { USER_TENANT_ACCESS_MODULE } from '@/store/user-tenant-access-module';

export const RETRIEVE_CURRENT_USER_PROFILE = 'retrieveCurrentUserProfile';
export const RETRIEVE_USER_PROFILES = 'retrieveUserProfiles';
export const RETRIEVE_USER_PROFILES_WITH_TENANT_ACCESS = 'retrieveUserProfilesWithTenantAccess';
export const UPDATE_AVATAR = 'updateAvatar';
export const REMOVE_AVATAR = 'removeAvatar';

const DEFAULT_ORDER_BY: OrderBy[] = [
    {
        attribute: 'user.@(:UserProfile:user).firstName',
        direction: 'ASC'
    },
    {
        attribute: 'user.@(:UserProfile:user).lastName',
        direction: 'ASC'
    }
];

export const actions: ActionTree<IUserProfile, unknown> = {
    async [RETRIEVE_CURRENT_USER_PROFILE](context) {
        const currentUser: IUser = context.rootGetters[`${SESSION_MODULE}/${GET_USER_CURRENT}`];
        if (currentUser?.dobjId !== undefined) {
            const query = queryBuilder.build(queryBuilder.op.and(queryBuilder.cmp.refersTo('user', currentUser.dobjId)));
            await context.dispatch(RETRIEVE_BY_QUERY, {
                query,
                properties: userProfileProperties.DEFAULT
            });
        }
    },
    async [RETRIEVE_USER_PROFILES](
        context,
        {
            applicationRoles,
            userIsInIds,
            userIsNotInIds,
            startIndex = 0,
            maxResults = 10,
            orderBy = DEFAULT_ORDER_BY,
            hideDisabledUser = true
        }: {
            applicationRoles?: number[];
            userIsInIds?: number[];
            userIsNotInIds?: number[];
            startIndex?: number;
            maxResults?: number;
            orderBy?: OrderBy[];
            hideDisabledUser: boolean;
        }
    ): Promise<number[]> {
        const userProfiles = await userProfilesManager.searchUserProfilesInTenant(
            applicationRoles,
            userIsInIds,
            userIsNotInIds,
            startIndex,
            maxResults,
            orderBy,
            hideDisabledUser
        );

        const typedDataObjectList = userProfiles.map((dataObject) => {
            return Object.assign(dataObject, { '@type': IapDataType.UserProfile });
        });
        void context.dispatch(STORE_DATA_OBJECTS, { dataObjectList: typedDataObjectList });

        const retrievedIdList = userProfiles.map((dataObject) => {
            return dataObject.dobjId;
        });
        return retrievedIdList;
    },
    async [RETRIEVE_USER_PROFILES_WITH_TENANT_ACCESS](
        context,
        {
            applicationRoles,
            userIsInIds,
            userIsNotInIds,
            hideDisabledUser = true,
            startIndex = 0,
            orderBy = DEFAULT_ORDER_BY
        }: {
            applicationRoles?: number[];
            userIsInIds?: number[];
            userIsNotInIds?: number[];
            hideDisabledUser?: boolean;
            startIndex?: number;
            orderBy?: OrderBy[];
        }
    ): Promise<number[]> {
        const userTenantAccessList = await userProfilesManager.searchUserProfilesWithTenantAccess(
            applicationRoles,
            userIsInIds,
            userIsNotInIds,
            hideDisabledUser,
            startIndex,
            orderBy
        );

        const userProfiles = (userTenantAccessList ?? []).map((userTenantAccess) => {
            if (userTenantAccess.user instanceof Object) {
                return userTenantAccess.user[UserCommonCalculatedFields.UserProfiles][0];
            } else {
                return [];
            }
        });
        const typedDataObjectList = userProfiles.map((dataObject) => {
            return Object.assign(dataObject, { '@type': IapDataType.UserProfile });
        });
        void context.dispatch(STORE_DATA_OBJECTS, { dataObjectList: typedDataObjectList });

        const tenantAccesses: IUserTenantAccess[] = (userTenantAccessList ?? []).map((userTenantAccess) => {
            if (userTenantAccess.user instanceof Object) {
                const user: IUser = userTenantAccess.user;
                userTenantAccess.user = user.dobjId;
                return userTenantAccess;
            } else {
                return undefined;
            }
        });
        context.commit(`${USER_TENANT_ACCESS_MODULE}/${RECEIVE_DATA_OBJECTS}`, { dataObjectList: tenantAccesses }, { root: true });

        return userProfiles.map((dataObject: IUserProfile) => {
            return dataObject.dobjId;
        });
    },
    async [UPDATE_AVATAR](context, { userProfileId, file, properties }: { userProfileId?: number; file?: File; properties?: string }): Promise<void> {
        const dataObjectList = await userProfilesManager.uploadNewAvatar(userProfileId, file, properties);
        context.commit(RECEIVE_DATA_OBJECTS, { dataObjectList });
    },
    async [REMOVE_AVATAR](context, { userProfileId, properties }: { userProfileId?: number; properties?: string }): Promise<void> {
        const dataObjectList = await userProfilesManager.removeAvatar(userProfileId, properties);
        context.commit(RECEIVE_DATA_OBJECTS, { dataObjectList });
    }
};
